import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronLeft, ChevronRight, Edit3 } from "lucide-react";
import Navbar from "../components/Navbar";
import CalendarMonth from "../components/CalendarMonth";
import { getTrainerProfile } from "../services/api";
import { utcTimeOfDayToLocal, formatTimeSlot } from "../utils/timeUtils";
import { useAuth } from "../contexts/AuthContext";

/** Helpers */
const fmtLongDate = (d: Date) =>
  d.toLocaleDateString("en-GB", { day: "2-digit", month: "long", year: "numeric" });
const fmtMonthYear = (d: Date) =>
  d.toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const addDays = (d: Date, delta: number) => {
  const n = new Date(d);
  n.setDate(n.getDate() + delta);
  return n;
};

type DayKey = "Sunday" | "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday";

type TimeSlot = {
  startTime: string;
  endTime: string;
};

type DayAvailability = {
  day: DayKey;
  timeSlots: TimeSlot[];
};

export default function TrainerAvailability() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // state
  const [monthDate, setMonthDate] = useState<Date>(new Date());
  const [selectedDay, setSelectedDay] = useState<number>(new Date().getDate());
  const [showCalendar, setShowCalendar] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availability, setAvailability] = useState<DayAvailability[]>([]);
  const [durationMinutes, setDurationMinutes] = useState<number>(0);

  const focusedDate = useMemo(
    () => new Date(monthDate.getFullYear(), monthDate.getMonth(), selectedDay),
    [monthDate, selectedDay]
  );

  // Parse time like "9:00 AM", "03:30 pm", "14:00" into minutes since midnight
  const parseTimeToMinutes = (t: string): number => {
    if (!t) return 0;
    const s = String(t).trim().toLowerCase();
    // Extract hh:mm
    const match = s.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)?$/i);
    if (!match) return 0;
    let hours = parseInt(match[1], 10);
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const suffix = match[3];
    if (suffix === 'am') {
      if (hours === 12) hours = 0;
    } else if (suffix === 'pm') {
      if (hours !== 12) hours += 12;
    }
    return hours * 60 + minutes;
  };

  // Update duration based on the first slot of the focused day
  useEffect(() => {
    if (!availability || availability.length === 0 || !focusedDate) {
      setDurationMinutes(0);
      return;
    }
    const dayName = focusedDate.toLocaleDateString('en-US', { weekday: 'long' }) as DayKey;
    const dayAvailability = availability.find(av => av.day === dayName);
    if (!dayAvailability || !Array.isArray(dayAvailability.timeSlots) || dayAvailability.timeSlots.length === 0) {
      setDurationMinutes(0);
      return;
    }
    const first = dayAvailability.timeSlots[0];
    const startM = parseTimeToMinutes(first.startTime);
    const endM = parseTimeToMinutes(first.endTime);
    const diff = Math.max(0, endM - startM);
    setDurationMinutes(diff);
  }, [availability, focusedDate]);

  // Load trainer data on component mount
  useEffect(() => {
    const loadTrainerData = async () => {
      if (!user?.id && !user?._id && !user?.userId) {
        console.log('No user ID found, cannot load trainer data');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get the trainer ID from user object
        const trainerId = user?.id || user?._id || user?.userId;
        console.log('Loading trainer data for ID:', trainerId);

        const trainerData = await getTrainerProfile(trainerId);
        console.log('Loaded trainer data:', trainerData);

        // Update availability from trainer data
        if (trainerData.availability && Array.isArray(trainerData.availability)) {
          setAvailability(trainerData.availability);
          console.log('Updated availability state:', trainerData.availability);
        }

      } catch (err) {
        console.error('Error loading trainer data:', err);
        setError('Failed to load trainer data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadTrainerData();
  }, [user?.id, user?._id, user?.userId, location.key]);

  // Calculate slots for the focused date
  const slots = useMemo(() => {
    if (!availability || availability.length === 0) return [];

    const dayName = focusedDate.toLocaleDateString('en-US', { weekday: 'long' }) as DayKey;
    const dayAvailability = availability.find(avail => avail.day === dayName);

    if (!dayAvailability || !dayAvailability.timeSlots || dayAvailability.timeSlots.length === 0) {
      return [];
    }

    // Convert time slots to display format - convert UTC to local time
    return dayAvailability.timeSlots.map(slot => {
      // Convert UTC times to local time for display
      let startTimeLocal = slot.startTime;
      let endTimeLocal = slot.endTime;

      // If times are in UTC 24-hour format, convert to local 12-hour format
      if (!startTimeLocal.includes('AM') && !startTimeLocal.includes('PM') && startTimeLocal.includes(':')) {
        startTimeLocal = utcTimeOfDayToLocal(startTimeLocal, '12hour', focusedDate);
      }
      if (!endTimeLocal.includes('AM') && !endTimeLocal.includes('PM') && endTimeLocal.includes(':')) {
        endTimeLocal = utcTimeOfDayToLocal(endTimeLocal, '12hour', focusedDate);
      }

      // Format as "9:00 AM - 5:00 PM"
      return formatTimeSlot(startTimeLocal, endTimeLocal);
    });
  }, [availability, focusedDate]);

  // nav behavior
  const prev = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (showCalendar) {
      setMonthDate((d) => {
        const prevMonth = new Date(d.getFullYear(), d.getMonth() - 1, 1);
        const minMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        // Prevent navigating to months before current month
        return prevMonth < minMonth ? d : prevMonth;
      });
    } else {
      const n = addDays(focusedDate, -1);
      // Prevent selecting past days
      if (n < today) return;
      setMonthDate(new Date(n.getFullYear(), n.getMonth(), 1));
      setSelectedDay(n.getDate());
    }
  };
  const next = () => {
    if (showCalendar) {
      setMonthDate((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
    } else {
      const n = addDays(focusedDate, +1);
      setMonthDate(new Date(n.getFullYear(), n.getMonth(), 1));
      setSelectedDay(n.getDate());
    }
  };

  if (loading) {
    return (
      <div
        className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="mx-auto w-full min-h-[100dvh]">
          <Navbar onBack={() => navigate(-1)} />
          <section className="px-5 pt-2">
            <h1
              className="font-manrope font-bold text-[20px] leading-[26px] tracking-[0%] text-[#2D2D2D]"
              style={{
                fontFamily: 'Manrope',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '26px',
                letterSpacing: '0%'
              }}
            >
              Availability
            </h1>
          </section>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="text-[#2D2D2D] text-[16px] mb-2">Loading availability...</div>
              <div className="text-[#8A8A8A] text-[14px]">Please wait while we fetch your schedule</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
        style={{ WebkitOverflowScrolling: "touch" }}
      >
        <div className="mx-auto w-full min-h-[100dvh]">
          <Navbar onBack={() => navigate(-1)} />
          <section className="px-5 pt-2">
            <h1
              className="font-manrope font-bold text-[20px] leading-[26px] tracking-[0%] text-[#2D2D2D]"
              style={{
                fontFamily: 'Manrope',
                fontWeight: 700,
                fontStyle: 'normal',
                fontSize: '20px',
                lineHeight: '26px',
                letterSpacing: '0%'
              }}
            >
              Availability
            </h1>
          </section>
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center px-5">
              <div className="text-[#D62422] text-[16px] mb-2">Error Loading Data</div>
              <div className="text-[#8A8A8A] text-[14px] mb-4">{error}</div>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className="text-[#D62422] font-extrabold underline"
                >
                  RETRY
                </button>
                <button
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="text-gray-600 font-semibold underline"
                >
                  Back to Role Selection
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto"
      style={{ WebkitOverflowScrolling: "touch" }}
    >
      <div className="mx-auto w-full min-h-[100dvh]">
        <Navbar onBack={() => navigate(-1)} />

        {/* Title */}
        <section className="px-5 pt-2">
          <h1
            className="font-manrope font-bold text-[20px] leading-[26px] tracking-[0%] text-[#2D2D2D]"
            style={{
              fontFamily: 'Manrope',
              fontWeight: 700,
              fontStyle: 'normal',
              fontSize: '20px',
              lineHeight: '26px',
              letterSpacing: '0%'
            }}
          >
            Availability
          </h1>
        </section>

        {/* Date / month header controls */}
        <section className="px-5 mt-3">
          <div className="flex items-center justify-between">
            <button
              onClick={prev}
              className="p-2 text-[#707070]"
              aria-label="Previous"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <button
              className="text-[14px] font-extrabold text-[#2D2D2D]"
              onClick={() => setShowCalendar((v) => !v)}
              aria-label="Toggle month calendar"
            >
              {showCalendar ? fmtMonthYear(monthDate) : fmtLongDate(focusedDate)}
            </button>

            <button
              onClick={next}
              className="p-2 text-[#707070]"
              aria-label="Next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* Calendar (only in month view) */}
        {showCalendar && (
          <section className="px-5 mt-2 preview-cal">
            <CalendarMonth
              monthDate={monthDate}
              selectedDays={[selectedDay]}
              onToggleDay={(day) => {
                const chosen = new Date(monthDate.getFullYear(), monthDate.getMonth(), day);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                // Ignore past days
                if (chosen < today) return;
                setSelectedDay(day);
                setShowCalendar(false);
              }}
            />
          </section>
        )}

        {/* Duration */}
        <section className="px-5">
          <div className="mt-4 text-center text-[12px] text-[#8A8A8A]">
            Duration: {durationMinutes} Minutes
          </div>
        </section>

        {/* Slots list (square corners, thin grey border) */}
        <section className="px-5 mt-4">
          <div className="space-y-3">
            {slots.length > 0 ? (
              slots.map((t) => (
                <button
                  key={t}
                  className="w-full h-[52px] border border-[#D9D9D9] bg-white grid place-items-center text-[14px] font-extrabold text-[#2D2D2D]"
                >
                  {t}
                </button>
              ))
            ) : (
              <div className="w-full h-[52px] border border-[#D9D9D9] bg-white grid place-items-center text-[14px] font-extrabold text-[#8A8A8A]">
                No availability for this day
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="px-5">
          <div className="mt-8 mb-16 flex justify-center">
            <button
              onClick={() => navigate("/trainer/edit-availability")}
              className="inline-flex items-center gap-2 text-[#D62422] font-extrabold"
            >
              <Edit3 className="w-4 h-4" />
              EDIT AVAILABILITY
            </button>
          </div>
        </section>
      </div>

      {/* Square corners inside the calendar area and hide duplicate navigation */}
      <style>{`
        .preview-cal button,
        .preview-cal [class*="rounded"] {
          border-radius: 0 !important;
        }
        .preview-cal > div > div:first-child {
          display: none !important;
        }
      `}</style>
    </div>
  );
}